import { useState } from "react";
import { Icons } from "@/components/ui/icons";
import { AppHeader } from "@/workshop/components/panels/app-header";
import { LeftSidebar } from "@/workshop/components/panels/left-sidebar";
import { RightPanel } from "@/workshop/components/panels/right-panel";
import { SpacePanel } from "@/workshop/components/panels/space-panel";
import type { SpacePanelTab } from "@/workshop/components/panels/space-panel";
import { InputBar } from "@/workshop/components/panels/input-bar";
import { InputBar as ProductionInputBar } from "@/components/panels/input-bar";
import { ExecutionStatusBar } from "@/workshop/components/panels/execution-status-bar";
import { Breadcrumb } from "@/workshop/components/panels/breadcrumb";
import { ActivityItem } from "@/workshop/components/panels/activity-item";
import { AgentList } from "@/workshop/components/panels/agent-list";
import { AddAgentPanel } from "@/workshop/components/panels/add-agent-panel";
import { AgentActivityPanel } from "@/workshop/components/panels/agent-activity-panel";
import { ExecutionSidebar } from "@/workshop/components/panels/execution-sidebar";
import { NotificationDropdown } from "@/workshop/components/panels/notification-dropdown";
import type { NotificationItem } from "@/workshop/components/panels/notification-dropdown";
import { NotificationToast } from "@/workshop/components/canvas/notification-toast";
import type { FrameEntry, TreeEntry, TaskEntry } from "@/workshop/components/panels/left-sidebar";
import {
  MOCK_EXECUTION_TODOS,
  MOCK_WORKFLOW_STEPS_RUNNING,
  MOCK_WORKFLOW_STEPS_APPROVAL,
  MOCK_WORKFLOW_STEPS_COMPLETED,
  MOCK_WORKFLOW_STEPS_FAILED,
} from "@/workshop/data/mock-workflows";

// --- Mock Data ---

const MOCK_FRAMES: FrameEntry[] = [
  { id: "f1", title: "API Integration Notes", icon: Icons.FileText, active: true, statusColor: "bg-accent" },
  { id: "f2", title: "Search: React patterns", icon: Icons.Search, statusColor: "bg-accent" },
  { id: "f3", title: "main.py", icon: Icons.Code, statusColor: "bg-muted" },
  { id: "f4", title: "Architecture diagram", icon: Icons.Image, statusColor: "bg-warning" },
];

const MOCK_TREE: TreeEntry[] = [
  { id: "t1", label: "denker-dolcetto", icon: Icons.FolderOpen, expanded: true, depth: 0 },
  { id: "t2", label: "backend", icon: Icons.FolderOpen, expanded: false, depth: 1 },
  { id: "t3", label: "frontend", icon: Icons.FolderOpen, expanded: true, depth: 1 },
  { id: "t4", label: "components", icon: Icons.FolderOpen, expanded: false, depth: 2 },
  { id: "t5", label: "pages", icon: Icons.FolderOpen, expanded: false, depth: 2 },
];

const MOCK_TASKS: TaskEntry[] = [
  { id: "task1", title: "Implement auth flow", checked: false, agentLetter: "C" },
  { id: "task2", title: "Write unit tests", checked: true, agentLetter: "R" },
  { id: "task3", title: "Review PR #42", checked: false, agentLetter: "A" },
];

const MOCK_DETAIL_SECTIONS = [
  {
    title: "Frame Info",
    rows: [
      { label: "Type", value: "Code Editor" },
      { label: "Created", value: "2026-03-10 14:32" },
      { label: "Last modified", value: "2 min ago" },
      { label: "Agent", value: "Coder" },
    ],
  },
  {
    title: "Properties",
    rows: [
      { label: "Language", value: "Python" },
      { label: "Lines", value: "247" },
      { label: "Status", value: "Active" },
    ],
  },
];

const MOCK_AGENTS = [
  { id: "a1", name: "Coder", color: "#818CF8", status: "active" as const },
  { id: "a2", name: "Researcher", color: "#F472B6", status: "idle" as const },
  { id: "a3", name: "Writer", color: "#34D399", status: "offline" as const },
];

const MOCK_ACTIVITIES = [
  { id: "a1", name: "Coder", color: "#818CF8", status: "active" as const, description: "Editing auth-service.py" },
  { id: "a2", name: "Researcher", color: "#F472B6", status: "paused" as const, description: "Waiting for web search results" },
  { id: "a3", name: "Writer", color: "#34D399", status: "idle" as const, description: "Completed documentation draft" },
];

const MOCK_ACTIVITY_FEED = [
  { timestamp: "14:32", description: "Coder started editing auth-service.py", dotColor: "var(--color-accent)" },
  { timestamp: "14:30", description: "Researcher completed web search for React patterns", dotColor: "var(--color-accent)" },
  { timestamp: "14:28", description: "Writer paused — waiting for review", dotColor: "var(--color-warning)" },
  { timestamp: "14:25", description: "Session started", dotColor: "var(--color-muted)" },
];

/* --- Space Panel mock data --- */

const MOCK_SPACE_ACTIVITIES = [
  { timestamp: "14:32", description: "Coder started editing auth-service.py", dotColor: "var(--color-accent)", source: "chat" as const },
  { timestamp: "14:30", description: "Researcher completed web search", dotColor: "var(--color-accent)", source: "chat" as const },
  { timestamp: "14:28", description: "Writer checked pending drafts", dotColor: "var(--color-muted)", source: "heartbeat" as const },
  { timestamp: "14:25", description: "Deploy monitor triggered — build failed", dotColor: "var(--color-danger)", source: "event" as const },
  { timestamp: "14:20", description: "Weekly report generation started", dotColor: "var(--color-accent)", source: "scheduled" as const },
  { timestamp: "14:15", description: "User asked: Research AI trends", dotColor: "var(--color-accent)", source: "chat" as const },
  { timestamp: "14:10", description: "Heartbeat — no pending items", dotColor: "var(--color-muted)", source: "heartbeat" as const },
  { timestamp: "14:05", description: "User commented on Deploy frame", dotColor: "var(--color-accent)", source: "comment" as const },
];

const MOCK_SPACE_TASKS = [
  { agentLetter: "C", agentColor: "#818CF8", title: "Fix auth middleware bug", status: "running" as const, source: "chat" as const, agentName: "Coder" },
  { agentLetter: "R", agentColor: "#F472B6", title: "Review schema change", status: "waiting-approval" as const, source: "event" as const, agentName: "Researcher", onApprove: () => console.log("[workshop] Approve"), onReject: () => console.log("[workshop] Reject") },
  { agentLetter: "W", agentColor: "#34D399", title: "Write API documentation", status: "queued" as const, source: "chat" as const, agentName: "Writer" },
  { agentLetter: "C", agentColor: "#818CF8", title: "Run integration tests", status: "queued" as const, source: "scheduled" as const, agentName: "Coder" },
  { agentLetter: "R", agentColor: "#F472B6", title: "Check competitor updates", status: "queued" as const, source: "heartbeat" as const, agentName: "Researcher" },
  { agentLetter: "V", agentColor: "#A78BFA", title: "Review code changes", status: "queued" as const, source: "comment" as const, agentName: "Reviewer" },
  { agentLetter: "U", agentColor: "#60A5FA", title: "Update API docs", status: "queued" as const, source: "user" as const },
];

const MOCK_NOTIFICATIONS: NotificationItem[] = [
  { id: "n1", type: "approval", agentLetter: "R", agentColor: "#F472B6", agentName: "Researcher", title: "Review schema change", description: "ALTER TABLE users ADD COLUMN preferences JSONB", timestamp: "2 min ago", read: false },
  { id: "n2", type: "approval", agentLetter: "W", agentColor: "#34D399", agentName: "Writer", title: "Send email to client", description: "Weekly progress report to team@acme.com", timestamp: "5 min ago", read: false },
  { id: "n3", type: "completed", agentLetter: "C", agentColor: "#818CF8", agentName: "Coder", title: "Auth middleware fix deployed", timestamp: "12 min ago", read: false },
  { id: "n4", type: "failed", agentLetter: "C", agentColor: "#818CF8", agentName: "Coder", title: "Integration tests failed", description: "3 tests failed in test_auth.py", timestamp: "18 min ago", read: true },
  { id: "n5", type: "info", agentLetter: "R", agentColor: "#F472B6", agentName: "Researcher", title: "Competitor analysis complete", timestamp: "1 hour ago", read: true },
];

const MOCK_NOTIFICATIONS_EMPTY: NotificationItem[] = [];

const MOCK_BREADCRUMBS = [
  { label: "Denker" },
  { label: "Projects", onClick: () => console.log("[workshop] Navigate to projects") },
  { label: "Dolcetto" },
];

// --- Page Component ---

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wider text-muted">
      {children}
    </h2>
  );
}

export function PanelsPage() {
  const [sidebarTab, setSidebarTab] = useState<"frames" | "browse">("frames");
  const [rightTab, setRightTab] = useState<"details" | "logs" | "actions">("details");
  const [spaceTab, setSpaceTab] = useState<SpacePanelTab>("activity");
  const [inputValue, setInputValue] = useState("");
  const [cursorInputValue, setCursorInputValue] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("a1");

  const productionCursorAgents = [
    { id: "denker", name: "Denker", avatar_seed: "Denker", isBuiltin: true },
    { id: "coder", name: "Coder", avatar_seed: "Coder" },
  ];
  const productionFocusedAgent = {
    id: "denker",
    adapterType: "claude_cli" as const,
    model: "sonnet",
    effort: "high" as const,
    contextPercent: 36,
  };
  const productionProviders = [
    { type: "claude_cli" as const, loggedIn: true, detected: true },
    { type: "codex_cli" as const, loggedIn: true, detected: true },
    { type: "opencode_cli" as const, loggedIn: null, detected: true },
  ];

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="panels-page">
      <h1 className="text-lg font-semibold text-primary">Panel Components</h1>
      <p className="text-sm text-secondary">
        Workshop showcase of all panel and overlay components with mock data.
      </p>

      {/* App Header */}
      <SectionTitle>App Header</SectionTitle>
      <div className="overflow-hidden rounded-lg border border-border">
        <AppHeader
          projectName="Dolcetto"
          spaceName="Canvas Alpha"
          spaces={[
            { id: "s1", label: "Research", active: true },
            { id: "s2", label: "Design", active: false },
            { id: "s3", label: "Development", active: false },
            { id: "s4", label: "Launch", active: false },
          ]}
          onSelectSpace={(id) => console.log("[workshop] Space selected:", id)}
          onAddSpace={() => console.log("[workshop] Add space")}
          onToggleSidebar={() => console.log("[workshop] Toggle sidebar")}
          onToggleRightPanel={() => console.log("[workshop] Toggle right panel")}
          notificationCount={3}
          onSearch={() => console.log("[workshop] Search")}
          onNotifications={() => console.log("[workshop] Notifications")}
          onUserMenu={() => console.log("[workshop] User menu")}
        />
      </div>

      {/* Breadcrumb */}
      <SectionTitle>Breadcrumb</SectionTitle>
      <div className="flex items-center gap-6">
        <Breadcrumb
          segments={MOCK_BREADCRUMBS}
          onChevronClick={() => console.log("[workshop] Chevron")}
          data-testid="breadcrumb-demo"
        />
      </div>

      {/* Sidebar + Right Panel side by side */}
      <SectionTitle>Left Sidebar + Right Panel</SectionTitle>
      <div className="flex h-[500px] overflow-hidden rounded-lg border border-border">
        <LeftSidebar
          activeTab={sidebarTab}
          frames={MOCK_FRAMES}
          treeItems={MOCK_TREE}
          tasks={MOCK_TASKS}
          onTabChange={setSidebarTab}
          onAddFrame={() => console.log("[workshop] Add frame")}
          onSelectFrame={(id) => console.log("[workshop] Select frame:", id)}
          onSelectTreeItem={(id) => console.log("[workshop] Select tree:", id)}
          onToggleTreeItem={(id) => console.log("[workshop] Toggle tree:", id)}
          onSelectTask={(id) => console.log("[workshop] Select task:", id)}
          onToggleTask={(id) => console.log("[workshop] Toggle task:", id)}
          onAddTask={() => console.log("[workshop] Add task")}
          execution={{
            executionState: "running",
            todos: MOCK_EXECUTION_TODOS,
            workflowSteps: MOCK_WORKFLOW_STEPS_RUNNING,
            workflowName: "Fix Failing CI",
            onStop: () => console.log("[workshop] Stop"),
          }}
        />
        <div className="flex-1 bg-canvas" />
        <RightPanel
          title="Code Frame"
          activeTab={rightTab}
          sections={MOCK_DETAIL_SECTIONS}
          onTabChange={setRightTab}
          onClose={() => console.log("[workshop] Close right panel")}
          onEdit={() => console.log("[workshop] Edit")}
          onDelete={() => console.log("[workshop] Delete")}
          logsContent={
            <div className="flex flex-col gap-1">
              {MOCK_ACTIVITY_FEED.map((item, i) => (
                <ActivityItem
                  key={i}
                  timestamp={item.timestamp}
                  description={item.description}
                  dotColor={item.dotColor}
                  data-testid={`log-item-${i}`}
                />
              ))}
            </div>
          }
        />
      </div>

      {/* Space Panel (Z-110: Activity + Tasks) */}
      <SectionTitle>Space Panel — Activity + Tasks (Z-110)</SectionTitle>
      <div className="flex h-[500px] gap-4">
        {/* Full interactive panel */}
        <SpacePanel
          activeTab={spaceTab}
          onTabChange={setSpaceTab}
          onClose={() => console.log("[workshop] Close space panel")}
          activities={MOCK_SPACE_ACTIVITIES}
          tasks={MOCK_SPACE_TASKS}
          className="h-full"
        />

        {/* Side-by-side: old right panel for comparison */}
        <div className="flex flex-col gap-2">
          <p className="text-[10px] text-muted">Old right panel (for comparison)</p>
          <RightPanel
            title="Code Frame"
            activeTab={rightTab}
            sections={MOCK_DETAIL_SECTIONS}
            onTabChange={setRightTab}
            onClose={() => console.log("[workshop] Close")}
            onEdit={() => console.log("[workshop] Edit")}
            onDelete={() => console.log("[workshop] Delete")}
            className="h-full"
          />
        </div>
      </div>

      {/* Input Bar + Execution Status */}
      <SectionTitle>Input Bar + Execution Status</SectionTitle>
      <div className="flex max-w-xl flex-col gap-2">
        <ExecutionStatusBar runningCount={2} />
        <InputBar
          value={inputValue}
          onChange={setInputValue}
          onSubmit={() => {
            console.log("[workshop] Submit:", inputValue);
            setInputValue("");
          }}
          agents={[
            { id: "a1", name: "Coder", color: "#30D158" },
            { id: "a2", name: "Researcher", color: "#60A5FA" },
            { id: "a3", name: "Writer", color: "#818CF8" },
          ]}
          placeholder="Ask your agents anything..."
        />
        <InputBar
          value=""
          onChange={() => {}}
          onSubmit={() => {}}
          disabled
          placeholder="Disabled input bar..."
        />
      </div>

      <SectionTitle>Production Cursor Input Bar</SectionTitle>
      <div className="flex max-w-3xl flex-col gap-3">
        <div className="relative h-36 overflow-hidden rounded-lg border border-glass-stroke bg-[#080A0F]">
          <ProductionInputBar
            value={cursorInputValue}
            onChange={setCursorInputValue}
            onSubmit={() => {
              console.log("[workshop] Cursor submit:", cursorInputValue);
              setCursorInputValue("");
            }}
            agents={productionCursorAgents}
            selectedAgentIds={new Set(["denker"])}
            onSelectAgents={() => {}}
            placeholder="Ask Denker anything..."
            cursorAnchor={{ type: "screen", localX: 4, localY: 40 }}
            compact
            defaultAgentName="Denker"
            focusedAgent={productionFocusedAgent}
            providers={productionProviders}
            onUpdateAgentModel={async () => {}}
            onUpdateAgentEffort={async () => {}}
          />
        </div>
        <div className="relative h-36 overflow-hidden rounded-lg border border-glass-stroke bg-[#080A0F]">
          <ProductionInputBar
            value="Make this plan sharper"
            onChange={() => {}}
            onSubmit={() => console.log("[workshop] Cursor submit with text")}
            agents={productionCursorAgents}
            selectedAgentIds={new Set(["denker"])}
            onSelectAgents={() => {}}
            placeholder="Ask Denker anything..."
            cursorAnchor={{ type: "screen", localX: 4, localY: 40 }}
            compact
            defaultAgentName="Denker"
            focusedAgent={productionFocusedAgent}
            providers={productionProviders}
            onUpdateAgentModel={async () => {}}
            onUpdateAgentEffort={async () => {}}
          />
        </div>
      </div>

      {/* Activity Items */}
      <SectionTitle>Activity Feed Items</SectionTitle>
      <div className="max-w-sm rounded-lg border border-border p-3">
        {MOCK_ACTIVITY_FEED.map((item, i) => (
          <ActivityItem
            key={i}
            timestamp={item.timestamp}
            description={item.description}
            dotColor={item.dotColor}
            data-testid={`activity-${i}`}
          />
        ))}
      </div>

      {/* Agent Panels — side by side */}
      <SectionTitle>Agent List / Add Agent / Activity</SectionTitle>
      <div className="flex flex-wrap gap-4">
        <AgentList
          agents={MOCK_AGENTS}
          selectedId={selectedAgent}
          onSelect={setSelectedAgent}
          onAdd={() => console.log("[workshop] Add agent")}
        />
        <AddAgentPanel
          onSelect={(id) => console.log("[workshop] Select template:", id)}
          onClose={() => console.log("[workshop] Close add agent")}
        />
        <AgentActivityPanel
          activities={MOCK_ACTIVITIES}
          onActivityClick={(id) => console.log("[workshop] Activity click:", id)}
        />
      </div>

      {/* Input Bar — @mention states */}
      <SectionTitle>Input Bar — @mention states</SectionTitle>

      {/* State: agent already mentioned in text */}
      <div className="max-w-xl">
        <p className="mb-2 text-xs text-muted">Agent already mentioned</p>
        <InputBar
          value="@Coder fix the auth module"
          onChange={() => {}}
          onSubmit={() => {}}
          agents={[
            { id: "a1", name: "Coder", color: "#30D158" },
            { id: "a2", name: "Researcher", color: "#60A5FA" },
            { id: "a3", name: "Writer", color: "#818CF8" },
          ]}
          placeholder="Ask your agents anything..."
        />
      </div>

      {/* State: popup open — needs room above for the popup to expand upward */}
      <div className="max-w-xl pt-64">
        <p className="mb-2 text-xs text-muted">Selecting an agent (@mention popup open)</p>
        <InputBar
          value="@"
          onChange={() => {}}
          onSubmit={() => {}}
          initialMentionOpen
          agents={[
            { id: "a1", name: "Coder", color: "#30D158" },
            { id: "a2", name: "Researcher", color: "#60A5FA" },
            { id: "a3", name: "Writer", color: "#818CF8" },
          ]}
          placeholder="Ask your agents anything..."
        />
      </div>

      {/* Execution Sidebar */}
      <SectionTitle>Execution Sidebar</SectionTitle>
      <div className="flex flex-wrap gap-6">
        {/* Todos + workflow steps — running */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-muted">Todos + workflow — running</span>
          <div className="h-80 w-60 rounded-lg border border-glass-stroke bg-glass-fill p-3" data-testid="exec-sidebar-running">
            <ExecutionSidebar
              executionState="running"
              todos={MOCK_EXECUTION_TODOS}
              workflowSteps={MOCK_WORKFLOW_STEPS_RUNNING}
              workflowName="Weekly Report"
              onStop={() => console.log("[workshop] Stop")}
            />
          </div>
        </div>

        {/* Workflow steps — approval */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-muted">Workflow — approval</span>
          <div className="h-80 w-60 rounded-lg border border-glass-stroke bg-glass-fill p-3" data-testid="exec-sidebar-approval">
            <ExecutionSidebar
              executionState="approval"
              todos={[]}
              workflowSteps={MOCK_WORKFLOW_STEPS_APPROVAL}
              workflowName="Daily Report"
              onApprove={() => console.log("[workshop] Approve")}
              onReject={() => console.log("[workshop] Reject")}
            />
          </div>
        </div>

        {/* Workflow steps — completed */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-muted">Workflow — completed</span>
          <div className="h-80 w-60 rounded-lg border border-glass-stroke bg-glass-fill p-3" data-testid="exec-sidebar-completed">
            <ExecutionSidebar
              executionState="completed"
              todos={[]}
              workflowSteps={MOCK_WORKFLOW_STEPS_COMPLETED}
              workflowName="Cleanup Deps"
              onRun={() => console.log("[workshop] Run")}
            />
          </div>
        </div>

        {/* Todos only — running (freeform) */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-muted">Todos only — running</span>
          <div className="h-80 w-60 rounded-lg border border-glass-stroke bg-glass-fill p-3" data-testid="exec-sidebar-todos-only">
            <ExecutionSidebar
              executionState="running"
              todos={MOCK_EXECUTION_TODOS}
              workflowSteps={[]}
              onStop={() => console.log("[workshop] Stop")}
            />
          </div>
        </div>

        {/* Todos only — idle */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-muted">Todos only — idle</span>
          <div className="h-80 w-60 rounded-lg border border-glass-stroke bg-glass-fill p-3" data-testid="exec-sidebar-idle">
            <ExecutionSidebar
              executionState="idle"
              todos={MOCK_EXECUTION_TODOS}
              workflowSteps={[]}
              onRun={() => console.log("[workshop] Run")}
            />
          </div>
        </div>

        {/* Workflow steps — failed */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-muted">Workflow — failed</span>
          <div className="h-80 w-60 rounded-lg border border-glass-stroke bg-glass-fill p-3" data-testid="exec-sidebar-failed">
            <ExecutionSidebar
              executionState="failed"
              todos={[]}
              workflowSteps={MOCK_WORKFLOW_STEPS_FAILED}
              workflowName="Build & Deploy"
              onRun={() => console.log("[workshop] Run")}
            />
          </div>
        </div>

        {/* Empty state */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-muted">Empty state — idle</span>
          <div className="h-80 w-60 rounded-lg border border-glass-stroke bg-glass-fill p-3" data-testid="exec-sidebar-empty">
            <ExecutionSidebar
              executionState="idle"
              todos={[]}
              workflowSteps={[]}
              onRun={() => console.log("[workshop] Run")}
            />
          </div>
        </div>
      </div>

      {/* Notification Dropdown (Z-112) */}
      <SectionTitle>Notification Dropdown (Z-112)</SectionTitle>
      <div className="flex flex-wrap gap-6">
        {/* With notifications */}
        <div>
          <p className="mb-2 text-xs text-muted">With notifications (3 unread)</p>
          <NotificationDropdown
            notifications={MOCK_NOTIFICATIONS}
            onClickItem={(id) => console.log("[workshop] Click notification:", id)}
            onMarkAllRead={() => console.log("[workshop] Mark all read")}
            onDismiss={(id) => console.log("[workshop] Dismiss:", id)}
          />
        </div>
        {/* Empty state */}
        <div>
          <p className="mb-2 text-xs text-muted">Empty state</p>
          <NotificationDropdown
            notifications={MOCK_NOTIFICATIONS_EMPTY}
          />
        </div>
      </div>

      {/* Notification Toast (Z-112) */}
      <SectionTitle>Notification Toast (Z-112)</SectionTitle>
      <div className="flex flex-wrap gap-4">
        {/* Approval toast — high risk */}
        <div>
          <p className="mb-2 text-xs text-muted">Approval (high risk) — persists</p>
          <NotificationToast
            type="approval"
            agentLetter="R"
            agentColor="#F472B6"
            agentName="Researcher"
            title="Review schema change"
            description="ALTER TABLE users ADD COLUMN preferences JSONB"
            riskLevel="high"
            onView={() => console.log("[workshop] View")}
            onDismiss={() => console.log("[workshop] Dismiss")}
          />
        </div>
        {/* Approval toast — low risk */}
        <div>
          <p className="mb-2 text-xs text-muted">Approval (low risk)</p>
          <NotificationToast
            type="approval"
            agentLetter="W"
            agentColor="#34D399"
            agentName="Writer"
            title="Send email to client"
            description="Weekly progress report to team@acme.com"
            riskLevel="low"
            onView={() => console.log("[workshop] View")}
            onDismiss={() => console.log("[workshop] Dismiss")}
          />
        </div>
        {/* Completed toast */}
        <div>
          <p className="mb-2 text-xs text-muted">Completed — auto-dismiss 5s</p>
          <NotificationToast
            type="completed"
            agentLetter="C"
            agentColor="#818CF8"
            agentName="Coder"
            title="Auth middleware fix deployed"
            onView={() => console.log("[workshop] View")}
            onDismiss={() => console.log("[workshop] Dismiss")}
          />
        </div>
        {/* Failed toast */}
        <div>
          <p className="mb-2 text-xs text-muted">Failed — auto-dismiss 5s</p>
          <NotificationToast
            type="failed"
            agentLetter="C"
            agentColor="#818CF8"
            agentName="Coder"
            title="Integration tests failed"
            description="3 tests failed in test_auth.py"
            onView={() => console.log("[workshop] View")}
            onDismiss={() => console.log("[workshop] Dismiss")}
          />
        </div>
      </div>

      <div className="h-16" />
    </div>
  );
}
